// eslint-disable-next-line check-file/filename-naming-convention
import { useState } from 'react';

import { useMutation, useQuery } from '@tanstack/react-query';
import { ChevronLeft, CircleCheck, Mail, MapPin } from 'lucide-react';
import { Navigate, type To, useNavigate, useParams } from 'react-router-dom';

import { TabFormProvider } from '@/components/features/account-manager/form-vendor/am-content';
import FormBank from '@/components/features/account-manager/form-vendor/form-bank';
import { Document } from '@/components/features/account-manager/form-vendor/form-document';
import FormMain from '@/components/features/account-manager/form-vendor/form-main';
import FormPic from '@/components/features/account-manager/form-vendor/form-pic';
import FormSegmentation from '@/components/features/account-manager/form-vendor/form-segmentation';
import InputControl from '@/components/form/input-control';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { WithStateQuery } from '@/components/ui/with-state';
import { StatusDocument } from '@/constants/status';
import { useResponse } from '@/hooks/use-dialog';
import { cn } from '@/lib/utils';
import HookFormProvider from '@/providers/hook-form-providers';
import { fileQueries } from '@/queries/file/file.queries';
import { userQueries } from '@/queries/user/user.queries';
import {
	vendorMutations,
	vendorQueries
} from '@/queries/vendor/vendor.queries';
import { getInitials } from '@/services/string';
import { useDialogStore } from '@/store';
import { mainSchema } from '@/types/form/account-manager/form';
import { loginSchema } from '@/types/form/auth/login';
import { Roles } from '@/types/roles';
import type { VendorDocumentDataResponse } from '@/types/vendor/vendor';

export type TabId =
	| 'login'
	| 'main'
	| 'bank'
	| 'segmentation'
	| 'document'
	| 'vao';

function CoverImage({ fileKey }: Readonly<{ fileKey: string }>) {
	const file = useQuery(fileQueries.getPresign({ key: fileKey }));
	return (
		<WithStateQuery state={file}>
			{({ data: { url } }) => (
				<img
					src={url}
					alt="cover-image"
					className="aspect-video h-32 w-full overflow-hidden bg-auto"
				/>
			)}
		</WithStateQuery>
	);
}

function AvatarProfile({ fileKey }: Readonly<{ fileKey: string }>) {
	const file = useQuery(fileQueries.getPresign({ key: fileKey }));

	return (
		<WithStateQuery state={file}>
			{({ data: { url } }) => <AvatarImage src={url} className="h-full w-full" />}
		</WithStateQuery>
	);
}

export default function VendorDetail({
	vendorId
}: {
	vendorId?: number | null
}) {
	const navigate = useNavigate();
	const params = useParams() as { id?: string };

	const id = (vendorId ?? params.id) as string;

	const [tab, setTab] = useState<TabId>('login');

	const onOpen = useDialogStore((s) => s.onOpenChange);
	const setDialog = useDialogStore((s) => s.setStore);
	const setAction = useDialogStore((s) => s.setAction);
	const { onError, onSuccess } = useResponse();

	const user = useQuery(userQueries.user());
	const vendor = useQuery(vendorQueries.vendor(id));
	const vendorDocument = useQuery(vendorQueries.vendorDocument(id));

	const submitRequest = useMutation(vendorMutations.approveRequest(id));

	const allowedRoles = [
		Roles.Procurement,
		Roles.Compliance,
		Roles.VendorAuthorizedOfficer
	];

	const hasValidRole =
		user.data?.data.roleId && allowedRoles.includes(user.data?.data.roleId);

	if (!hasValidRole) {
		return <Navigate to="/home" replace />;
	}

	const checkAllowApproval = (
		role: Roles | null,
		status: VendorDocumentDataResponse['statusId']
	) =>
		(role === Roles.Procurement &&
			status === StatusDocument.WaitingApprovalPam) ||
		(role === Roles.Compliance && status === StatusDocument.WaitingApprovalCcm);

	const isApproval = (role: Roles | null) =>
		role === Roles.Procurement || role === Roles.Compliance;

	function handleSubmit(haveRevised: boolean, lastProcess = false) {
		setDialog('variant', 'warning');
		setDialog(
			'title',
			haveRevised ? 'Minta Perbaikan Dokumen?' : 'Kirim Data Persetujuan'
		);
		setDialog(
			'description',
			haveRevised ? (
				'Vendor akan melakukan revisi dan mengirimkan kembali dokumen yang Anda tolak'
			) : (
				<span className="flex flex-col items-center justify-center gap-4">
					<span>
						Seluruh data yang dikirim akan melalui proses verifikasi lanjutan.
					</span>
					<span>
						Mohon pastikan kebenaran dan kelengkapan informasi sebelum mengirimkan
						dokumen.
					</span>
				</span>
			)
		);
		setDialog('cancelText', 'Batal');
		setDialog('confirmText', !haveRevised ? 'Ya, Kirim' : 'Ya, Lanjutkan');
		setAction('onConfirm', () =>
			submitRequest.mutate(undefined, {
				onSuccess: () =>
					onSuccess({
						message: haveRevised
							? 'Permintaan Perbaikan Dokumen Berhasil Dikirim'
							: lastProcess
								? 'Dokumen berhasil dikirim'
								: 'Seluruh dokumen Anda telah berhasil dikirim. Silakan menunggu proses verifikasi oleh CCM.'
					}),
				onError: ({ message }) => onError({ message })
			})
		);
		setAction('onCancel', () => onOpen(true));
		onOpen(true);
	}

	return (
		<>
			{params.id ? (
				<Card className="mt-2 p-0">
					<CardHeader className="flex flex-row items-center gap-3 px-3 py-3 text-xl font-bold">
						<div className="flex justify-center gap-3">
							<Button
								className="size-6 rounded-full bg-indigo-50 hover:bg-indigo-100"
								type="button"
								onClick={() => navigate(-1 as To, { viewTransition: true })}
								asChild
								size={'icon'}
							>
								<ChevronLeft color="#005094" size={10} />
							</Button>
						</div>
						Detail Vendor
					</CardHeader>
				</Card>
			) : null}
			<WithStateQuery state={user}>
				{({ data: { roleId } }) => (
					<>
						<Card className="py-0">
							<CardHeader className="relative p-5">
								<div className="to-primary/70 relative h-32 w-full rounded-md bg-linear-to-r from-gray-300">
									<WithStateQuery state={vendor}>
										{({
											data: {
												main: { cover }
											}
										}) => (cover ? <CoverImage fileKey={cover} /> : null)}
									</WithStateQuery>
								</div>
								<WithStateQuery state={vendor}>
									{({
										data: {
											main: { name, photo, city_name, phone_number, province_name },
											login: { email }
										}
									}) => (
										<div className="-mt-10 flex items-end gap-3">
											<div className="relative size-24">
												<Avatar className="size-24 rounded-full border border-black/10">
													{photo ? <AvatarProfile fileKey={photo} /> : null}
													<AvatarFallback className="rounded-full border border-gray-400 text-3xl">
														{getInitials(name ?? '')}
													</AvatarFallback>
												</Avatar>
											</div>
											<div className="flex h-full flex-col text-black">
												<p className="text-2xl">{name}</p>
												<div className="text-secondary flex items-center justify-center gap-2">
													<Mail size={16} />
													<p className="text-sm">{email}</p>
													<MapPin size={16} />
													<p className="text-sm">
														{[province_name, city_name].filter(Boolean).join(' - ')}
													</p>
													<img
														src="/icon/whatsapp.webp"
														alt="icon-upload"
														className="size-4"
													/>
													<p className="text-sm">{phone_number}</p>
												</div>
											</div>
										</div>
									)}
								</WithStateQuery>
							</CardHeader>
							<CardContent className="px-5">
								<Tabs
									defaultValue="login"
									value={tab}
									onValueChange={(tab) => setTab(tab as TabId)}
								>
									<div className="bg-primary-foreground flex overflow-x-auto border-b border-b-neutral-200">
										<TabsList className="border-secondary/40 rounded-none border-b-0 bg-transparent px-0 pb-0">
											<TabsTrigger
												value="login"
												className="data-[state=active]:border-b-primary text-secondary flex h-full items-center justify-center gap-2 rounded-none border-b-0 py-2 data-[state=active]:border-b-2 data-[state=active]:bg-transparent data-[state=active]:font-bold data-[state=active]:text-black data-[state=active]:shadow-none"
												onClick={() => setTab('login')}
											>
												<span>Data Login</span>
												<CircleCheck className="text-green-500" />
											</TabsTrigger>
											<TabsTrigger
												value="main"
												className="data-[state=active]:border-b-primary text-secondary flex h-full items-center justify-center gap-2 rounded-none border-b-0 py-2 data-[state=active]:border-b-2 data-[state=active]:bg-transparent data-[state=active]:font-bold data-[state=active]:text-black data-[state=active]:shadow-none"
												onClick={() => setTab('main')}
											>
												<span>Data Utama</span>
												<CircleCheck className={cn('text-green-500')} />
											</TabsTrigger>
											<TabsTrigger
												value="bank"
												className="data-[state=active]:border-b-primary text-secondary flex h-full items-center justify-center gap-2 rounded-none border-b-0 py-2 data-[state=active]:border-b-2 data-[state=active]:bg-transparent data-[state=active]:font-bold data-[state=active]:text-black data-[state=active]:shadow-none"
												onClick={() => setTab('bank')}
											>
												<span>Data Bank</span>
												<CircleCheck className={cn('text-green-500')} />
											</TabsTrigger>
											<TabsTrigger
												value="segmentation"
												className="data-[state=active]:border-b-primary text-secondary flex h-full items-center justify-center gap-2 rounded-none border-b-0 py-2 data-[state=active]:border-b-2 data-[state=active]:bg-transparent data-[state=active]:font-bold data-[state=active]:text-black data-[state=active]:shadow-none"
												onClick={() => setTab('segmentation')}
											>
												<span>Data Segmentasi</span>
												<CircleCheck className={cn('text-green-500')} />
											</TabsTrigger>
											<TabsTrigger
												value="document"
												className="data-[state=active]:border-b-primary text-secondary flex h-full items-center justify-center gap-2 rounded-none border-b-0 py-2 data-[state=active]:border-b-2 data-[state=active]:bg-transparent data-[state=active]:font-bold data-[state=active]:text-black data-[state=active]:shadow-none"
												onClick={() => setTab('document')}
											>
												<span>Data Dokumen</span>
												<CircleCheck className={cn('text-green-500')} />
											</TabsTrigger>
											<TabsTrigger
												value="vao"
												className="data-[state=active]:border-b-primary text-secondary flex h-full items-center justify-center gap-2 rounded-none border-b-0 py-2 data-[state=active]:border-b-2 data-[state=active]:bg-transparent data-[state=active]:font-bold data-[state=active]:text-black data-[state=active]:shadow-none"
												onClick={() => setTab('vao')}
											>
												<span>Data Penanggung Jawab</span>
												<CircleCheck className={cn('text-green-500')} />
											</TabsTrigger>
										</TabsList>
									</div>
									<TabFormProvider>
										<TabsContent value="login">
											<WithStateQuery state={vendor}>
												{({
													data: {
														main: { name },
														login: { email }
													}
												}) => (
													<HookFormProvider
														schema={loginSchema}
														onSubmit={() => {}}
														defaultValues={{
															name,
															email
														}}
														className="my-2"
													>
														<div className="grid w-full grid-cols-1 gap-x-4 gap-y-2 md:grid-cols-2">
															<p className="col-span-full text-xl font-medium">Data Login</p>
															<InputControl name="name" label="Nama Perusahaan" isDisabled />
															<InputControl name="email" label="Email Perusahaan" isDisabled />
														</div>
													</HookFormProvider>
												)}
											</WithStateQuery>
										</TabsContent>
										<TabsContent value="main">
											<WithStateQuery state={vendor}>
												{({
													data: {
														main: {
															name,
															company_id,
															business_area_id,
															qualification_id,
															type_cooperations,
															address,
															website,
															phone_number,
															province_id,
															city_id,
															district_id,
															postal_code,
															village_id,
															npwp,
															npwp_address,
															npwp_file_name,
															npwp_name,
															pic_email,
															pic_name,
															pic_nik,
															pic_phone_number
														}
													}
												}) => (
													<HookFormProvider
														schema={mainSchema}
														defaultValues={{
															name,
															company_id,
															business_area_id,
															qualification_id,
															type_cooperations: type_cooperations
																?.map((v) => v.id)
																?.filter(Boolean),
															website,
															phone_number,
															address,
															province_id,
															city_id,
															district_id,
															village_id,
															postal_code,
															npwp,
															npwp_name,
															npwp_address,
															npwp_file_name,
															pic_email,
															pic_name,
															pic_nik,
															pic_phone_number
														}}
														onSubmit={() => {}}
														className="my-2"
														formOptions={{ disabled: true }}
													>
														<FormMain vendorName={name} isDisabled />
													</HookFormProvider>
												)}
											</WithStateQuery>
										</TabsContent>
										<TabsContent value="bank">
											<WithStateQuery state={vendor}>
												{({
													data: {
														bank,
														main: { name }
													}
												}) => <FormBank vendorName={name} banks={bank} isDisabled />}
											</WithStateQuery>
										</TabsContent>
										<TabsContent value="segmentation">
											<WithStateQuery state={vendor}>
												{({ data: { segmentation } }) => (
													<FormSegmentation segmentations={segmentation} isDisabled />
												)}
											</WithStateQuery>
										</TabsContent>
										<TabsContent value="document">
											<div className="grid grid-cols-1 gap-3 md:grid-cols-2">
												<WithStateQuery state={[vendor, vendorDocument]}>
													{([
														{
															data: {
																main: { name }
															}
														},
														{ data }
													]) =>
														data.map((doc) => (
															<Document
																{...doc}
																key={doc.id}
																isDisabled
																vendorName={name}
																allowApproval={checkAllowApproval(roleId, doc.statusId)}
																isApproval={isApproval(roleId)}
															/>
														))
													}
												</WithStateQuery>
											</div>
										</TabsContent>
										<TabsContent value="vao">
											<WithStateQuery state={vendor}>
												{({
													data: {
														personResponsible,
														main: { name }
													}
												}) => (
													<FormPic pics={personResponsible} vendorName={name} isDisabled />
												)}
											</WithStateQuery>
										</TabsContent>
									</TabFormProvider>
								</Tabs>
							</CardContent>
						</Card>
						<WithStateQuery state={[vendor, vendorDocument]}>
							{([
								{
									data: {
										canContinue,
										main: { status }
									}
								},
								{ data }
							]) =>
								checkAllowApproval(roleId, status) ? (
									<Card className="p-0">
										<CardContent className="flex flex-row items-center justify-between px-3">
											<span className="font-bold">Kirim Keseluruhan Data Vendor</span>
											<div className="flex items-stretch justify-center gap-3">
												<Button
													className="h-auto px-8"
													disabled={!canContinue}
													type="button"
													onClick={() =>
														handleSubmit(
															data.some((v) =>
																[StatusDocument.RevisedPam, StatusDocument.RevisedCcm].includes(
																	v.statusId as StatusDocument
																)
															),
															status === StatusDocument.WaitingApprovalCcm
														)
													}
												>
													Kirim
												</Button>
											</div>
										</CardContent>
									</Card>
								) : null
							}
						</WithStateQuery>
					</>
				)}
			</WithStateQuery>
		</>
	);
}
